import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FaArrowLeft, FaMapMarkerAlt, FaVideo } from "react-icons/fa";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
// import Alert removed after migration to react-toastify
import { toast } from "react-toastify";
import Badge from '../../components/Badge';
import Button from "../../components/Button";
import Card from "../../components/Card";
import InfoRow from "../../components/InfoRow";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";

export default function TutorConfirm() {
  const { bookingId, tutorId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // toast is now used for notifications

  useEffect(() => {
    loadBooking();
  }, []);

  const loadBooking = async () => {
    try {
      const response = await api.get(`/bookings/${bookingId}`);
      setBooking(response.booking);
    } catch (error) {
      setError("Failed to load booking");
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (action) => {
    setSubmitting(true);
    setError("");

    try {
      await api.post(`/bookings/${bookingId}/tutors/${tutorId}/respond`, {
        action,
      });
      toast.success(`Booking ${action === "accept" ? "accepted" : "declined"} successfully!`);
      navigate("/tutor/dashboard");
    } catch (err) {
      const errorMsg = err.error || "Failed to respond";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  if (!booking) {
    return <div className="p-8">Booking not found</div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Booking Request" />

      <Card>
        {/* Alert removed after migration to react-toastify; error can be shown with toast or inline */}

        <div className="space-y-4">
          <InfoRow label="Subject" value={booking.subjectId?.name} />

          <InfoRow label="Student">
            {booking.studentId?.name?.first} {booking.studentId?.name?.last}
          </InfoRow>

          <div>
            <h2 className="font-semibold text-text">Preferred Times</h2>
            <ul className="list-disc list-inside text-text opacity-80">
              {booking.preferredTimes?.map((time, index) => (
                <li key={index}>
                  {dayjs(time.date).format("MMM D, YYYY")} from {time.start} to{" "}
                  {time.end}
                </li>
              ))}
            </ul>
          </div>

          <InfoRow label="Session Type">
            <span className="flex items-center gap-2">
              {booking.sessionType === 'in-person' ? (
                <>
                  <FaMapMarkerAlt className="flex-shrink-0" /> In-Person
                </>
              ) : (
                <>
                  <FaVideo className="flex-shrink-0" /> Online (Google Meet)
                </>
              )}
            </span>
          </InfoRow>

          {booking.sessionType === 'in-person' && booking.location && (
            <InfoRow label="Location" value={booking.location} />
          )}

          {booking.topicsToFocus && (
            <div>
              <h2 className="font-semibold text-text">Topics/Areas to Focus</h2>
              <div className="text-text opacity-80 whitespace-pre-wrap">
                {booking.topicsToFocus}
              </div>
            </div>
          )}

          <div>
            <h2 className="font-semibold text-text">Status</h2>
            <p className="capitalize text-text opacity-80">
              <Badge
                variant={booking.status === "confirmed" ? "success" : "error"}
                label={booking.status.replace("_", " ")}
                size="small"
                className="font-medium"
              />
            </p>
          </div>

          {booking.expiresAt && (
            <div>
              <h2 className="font-semibold text-text">Expires</h2>
              <p className="text-text opacity-80">
                {dayjs(booking.expiresAt).format("MMM D, YYYY h:mm A")}
              </p>
            </div>
          )}
        </div>

        {booking.status === "awaiting_tutor_confirmations" && (
          <div className="mt-6 flex gap-4">
            <Button
              onClick={() => handleRespond("accept")}
              loading={submitting}
              variant="secondary"
              className="flex-1"
            >
              Accept
            </Button>
            <Button
              onClick={() => handleRespond("decline")}
              loading={submitting}
              variant="danger"
              className="flex-1"
            >
              Decline
            </Button>
          </div>
        )}

        {booking.status === "confirmed" && (
          <div className="mt-6 p-4 bg-secondary/15 rounded-md">
            <p className="text-secondary">This booking has been confirmed.</p>
          </div>
        )}

        <div className="mt-6">
          <Link to="/tutor/dashboard" className="text-primary hover:opacity-80 flex items-center gap-2">
            <FaArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </Card>
    </div>
  );
}
